import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { TextareaBase, type TextareaBaseProps } from "@core/ui";

export type Props = TextareaBaseProps & {
  /** Whether field has a prefix element (affects border radius) */
  prefix?: boolean;
  /** Whether field has an icon (affects padding) */
  isWithIcon?: boolean;
};

/**
 * Styled textarea component.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Standalone usage
 * <Textarea
 *   name="message"
 *   value={message}
 *   onChange={(e) => setMessage(e.target.value)}
 *   error={!!errors.message}
 * />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      name,
      className,
      error,
      disabled,
      prefix,
      isWithIcon,
      onChange,
      onBlur,
      required: _required, // Filtered to prevent HTML5 validation bubbles
      ...props
    },
    ref
  ) => {
    return (
      <TextareaBase
        ref={ref}
        name={name}
        error={error}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        className={twMerge(
          // Base styles
          "border rounded-sm bg-transparent text-sm shadow-sm transition-colors px-3 py-2 w-full min-h-[100px]",
          // Placeholder
          "placeholder:text-brand-400",
          // Focus styles
          "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
          // Disabled state
          "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300 disabled:border-brand-300",
          // Error state
          error && "border-error-600 border-2",
          // Prefix modifier
          prefix && "rounded-l-none",
          // Icon modifier
          isWithIcon && "pl-10",
          className
        )}
        {...props}
      />
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;

